/**
 * Pure helpers for handing a new session from the system browser back into the
 * native app's WebView (they do not share storage).
 *
 * - The app keeps a random state and an ECDH private key in its own storage.
 * - The browser only ever sees the app's public key and the state.
 * - The callback page seals the session to that public key (ECDH P-256 +
 *   HKDF-SHA-256 + AES-GCM, bound to the state) and opens the app's own URL
 *   scheme with the ciphertext. Nothing is stored on a server.
 * - The app opens it with its private key and checks the state matches the
 *   attempt it started, so a sealed session can only land in the device and
 *   app instance that asked for it.
 */

export type NativeAppType = "pro" | "couple";

/**
 * The browser hand-off below is a custom protocol. SECURITY.md requires the
 * provider-supported, transaction-bound flow, so it stays OFF until a release
 * security review approves it (or a native, provider-supported flow replaces
 * it). While off, Google in the apps and Apple outside iOS ask people to use
 * email sign-in, and the start/callback pages refuse every request.
 * Flip only in code, after that review; tests switch it on to cover the logic.
 */
export const nativeBrowserOAuth = { enabled: false };
export type NativeOAuthProvider = "google" | "apple";

export const PENDING_KEY = "native_oauth_handoff_v1";
/** How long the app waits for a sign-in started in the system browser. */
export const PENDING_TTL_MS = 10 * 60 * 1000;
/** The callback page refuses sessions older than this. */
export const FRESH_SIGN_IN_MS = 10 * 60 * 1000;
export const CALLBACK_PATH = "/auth/native-callback";
export const START_PATH = "/auth/native-start";
export const RETURN_PATH = "/auth/native-return";

/** Each iOS app registers its own URL scheme (ios-couple / ios-pro Info.plist). */
export const APP_SCHEMES: Record<NativeAppType, string> = {
  couple: "everafterlist",
  pro: "everafterlistpro",
};

const B64URL = /^[A-Za-z0-9_-]+$/;
const isB64 = (v: unknown, min: number, max: number): v is string =>
  typeof v === "string" && v.length >= min && v.length <= max && B64URL.test(v);

/** 16 random bytes. */
export const isState = (v: unknown): v is string => isB64(v, 22, 22);
/** Uncompressed P-256 public key (65 bytes). */
export const isPublicKey = (v: unknown): v is string => isB64(v, 87, 87);
/** 12-byte AES-GCM IV. */
export const isIv = (v: unknown): v is string => isB64(v, 16, 16);
export const isCiphertext = (v: unknown): v is string => isB64(v, 16, 4096);
export const isNativeAppType = (v: unknown): v is NativeAppType => v === "pro" || v === "couple";

export const toBase64Url = (bytes: Uint8Array): string => {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const fromBase64Url = (value: string): Uint8Array<ArrayBuffer> => {
  const b64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

export interface PendingHandoff {
  v: 1;
  provider: NativeOAuthProvider;
  appType: NativeAppType;
  returnPath: string;
  state: string;
  publicKey: string;
  privateJwk: JsonWebKey;
  createdAt: number;
}

export interface SealedHandoff {
  ct: string;
  iv: string;
  epk: string;
}

export interface HandoffPayload {
  v: 1;
  /** State from the app's callback URL. */
  s: string;
  /** User ID of the session being handed over. */
  u: string;
  /** Refresh token of the new session. */
  rt: string;
}

const ECDH = { name: "ECDH", namedCurve: "P-256" } as const;
const HKDF_INFO = new TextEncoder().encode("everafterlist-native-handoff-v1");
const enc = (s: string) => new TextEncoder().encode(s);

const deriveAesKey = async (privateKey: CryptoKey, publicKey: CryptoKey, state: string) => {
  const shared = await crypto.subtle.deriveBits({ name: "ECDH", public: publicKey }, privateKey, 256);
  const hkdfKey = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: enc(state), info: HKDF_INFO },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
};

const importPublic = (raw: string) => crypto.subtle.importKey("raw", fromBase64Url(raw), ECDH, false, []);

/** App side: a fresh state and key pair for one sign-in attempt. */
export const createHandoffSecrets = async () => {
  const state = toBase64Url(crypto.getRandomValues(new Uint8Array(16)));
  const pair = (await crypto.subtle.generateKey(ECDH, true, ["deriveBits"])) as CryptoKeyPair;
  const publicKey = toBase64Url(new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey)));
  const privateJwk = await crypto.subtle.exportKey("jwk", pair.privateKey);
  return { state, publicKey, privateJwk };
};

/** Browser side: seal the payload so only the app that started sign-in can open it. */
export const sealForApp = async (appPublicKey: string, payload: HandoffPayload): Promise<SealedHandoff> => {
  if (!isPublicKey(appPublicKey) || !isState(payload.s)) throw new Error("Invalid sign-in link");
  const appKey = await importPublic(appPublicKey);
  const eph = (await crypto.subtle.generateKey(ECDH, true, ["deriveBits"])) as CryptoKeyPair;
  const key = await deriveAesKey(eph.privateKey, appKey, payload.s);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: enc(payload.s) },
    key,
    enc(JSON.stringify(payload)),
  );
  return {
    ct: toBase64Url(new Uint8Array(ct)),
    iv: toBase64Url(iv),
    epk: toBase64Url(new Uint8Array(await crypto.subtle.exportKey("raw", eph.publicKey))),
  };
};

/** App side: open a returned handoff. Throws if tampered with or sealed for another attempt. */
export const openHandoff = async (pending: PendingHandoff, sealed: SealedHandoff): Promise<HandoffPayload> => {
  if (!isCiphertext(sealed.ct) || !isIv(sealed.iv) || !isPublicKey(sealed.epk)) {
    throw new Error("Invalid sign-in handoff");
  }
  const privateKey = await crypto.subtle.importKey("jwk", pending.privateJwk, ECDH, false, ["deriveBits"]);
  const key = await deriveAesKey(privateKey, await importPublic(sealed.epk), pending.state);
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: fromBase64Url(sealed.iv), additionalData: enc(pending.state) },
    key,
    fromBase64Url(sealed.ct),
  );
  const payload = JSON.parse(new TextDecoder().decode(plain)) as HandoffPayload;
  if (payload?.v !== 1 || typeof payload.rt !== "string" || !payload.rt || typeof payload.u !== "string") {
    throw new Error("Invalid sign-in handoff");
  }
  if (payload.s !== pending.state) throw new Error("Sign-in state did not match");
  return payload;
};

/**
 * Where to land after sign-in, per app. Only the app's own sign-in screen is
 * allowed; that screen then routes by role exactly as it does for email sign-in.
 */
export const safeNativeReturnPath = (app: NativeAppType, requested?: string | null): string => {
  const fallback = app === "pro" ? "/pro/login" : "/auth";
  if (!requested || typeof requested !== "string") return fallback;
  let url: URL;
  try {
    url = new URL(requested, "https://app.invalid");
  } catch {
    return fallback;
  }
  if (url.origin !== "https://app.invalid") return fallback;
  if (app === "pro") return url.pathname === "/pro/login" ? "/pro/login" : fallback;
  if (url.pathname !== "/auth") return fallback;
  const redirect = url.searchParams.get("redirect");
  if (redirect && redirect.startsWith("/") && !redirect.startsWith("//") && !redirect.startsWith("/pro")) {
    return `/auth?redirect=${encodeURIComponent(redirect)}`;
  }
  return "/auth";
};

export const isNativeOAuthProvider = (v: unknown): v is NativeOAuthProvider => v === "google" || v === "apple";

/**
 * Legacy callback URL with the attempt in its query. Still accepted by the
 * callback page so a sign-in started just before an update can finish.
 */
export const buildCallbackUrl = (
  origin: string,
  app: NativeAppType,
  secrets: { publicKey: string; state: string },
) => {
  const url = new URL(CALLBACK_PATH, origin);
  url.searchParams.set("app", app);
  url.searchParams.set("k", secrets.publicKey);
  url.searchParams.set("s", secrets.state);
  return url.toString();
};

/**
 * First page the app opens in the in-app browser. It records the attempt in
 * that browser's own storage and then starts the provider sign-in, so the
 * provider only needs to return to a plain path on our site.
 */
export const buildStartUrl = (
  origin: string,
  app: NativeAppType,
  provider: NativeOAuthProvider,
  secrets: { publicKey: string; state: string },
) => {
  const url = new URL(START_PATH, origin);
  url.searchParams.set("app", app);
  url.searchParams.set("provider", provider);
  url.searchParams.set("k", secrets.publicKey);
  url.searchParams.set("s", secrets.state);
  return url.toString();
};

/** URL the callback page opens to hand control back to the app. */
export const buildReturnUrl = (app: NativeAppType, result: SealedHandoff | { error: string }) => {
  const params = new URLSearchParams(
    "error" in result ? { error: result.error } : { ct: result.ct, iv: result.iv, epk: result.epk },
  );
  return `${APP_SCHEMES[app]}:/${RETURN_PATH}?${params.toString()}`;
};

/**
 * In-app browser side of one attempt: only the app's public key, the state and
 * which app asked. Never a private key or a session. Lives in the in-app
 * browser's storage (which the app's WebView cannot read) for PENDING_TTL_MS,
 * and is removed as soon as the callback page uses it.
 */
export interface BrowserHandoff {
  v: 1;
  app: NativeAppType;
  provider: NativeOAuthProvider;
  k: string;
  s: string;
  /** Sign-in time of any session already in this browser, so it is never handed over. */
  prior: string | null;
  createdAt: number;
}

export const BROWSER_PENDING_KEY = "native_oauth_browser_v1";

export const parseStartParams = (search: string): Omit<BrowserHandoff, "v" | "createdAt" | "prior"> | null => {
  const q = new URLSearchParams(search);
  const app = q.get("app");
  const provider = q.get("provider");
  const k = q.get("k");
  const s = q.get("s");
  if (!isNativeAppType(app) || !isNativeOAuthProvider(provider) || !isPublicKey(k) || !isState(s)) return null;
  return { app, provider, k, s };
};

export const saveBrowserHandoff = (h: BrowserHandoff) => {
  window.localStorage.setItem(BROWSER_PENDING_KEY, JSON.stringify(h));
};

export const clearBrowserHandoff = () => {
  try {
    window.localStorage.removeItem(BROWSER_PENDING_KEY);
  } catch {
    // storage unavailable
  }
};

export const readBrowserHandoff = (now = Date.now()): BrowserHandoff | null => {
  try {
    const raw = window.localStorage.getItem(BROWSER_PENDING_KEY);
    if (!raw) return null;
    const h = JSON.parse(raw) as BrowserHandoff;
    const valid =
      h?.v === 1 &&
      isNativeAppType(h.app) &&
      isNativeOAuthProvider(h.provider) &&
      isPublicKey(h.k) &&
      isState(h.s) &&
      (h.prior === null || (typeof h.prior === "string" && h.prior.length <= 64)) &&
      typeof h.createdAt === "number" &&
      now - h.createdAt <= PENDING_TTL_MS &&
      h.createdAt <= now + 60_000;
    if (!valid) {
      clearBrowserHandoff();
      return null;
    }
    return h;
  } catch {
    clearBrowserHandoff();
    return null;
  }
};

export const savePending = (pending: PendingHandoff) => {
  window.localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
};

export const clearPending = () => {
  try {
    window.localStorage.removeItem(PENDING_KEY);
  } catch {
    // storage unavailable
  }
};

export const readPending = (now = Date.now()): PendingHandoff | null => {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingHandoff;
    const valid =
      p?.v === 1 &&
      isState(p.state) &&
      isPublicKey(p.publicKey) &&
      isNativeAppType(p.appType) &&
      typeof p.createdAt === "number" &&
      now - p.createdAt <= PENDING_TTL_MS &&
      p.createdAt <= now + 60_000;
    if (!valid) {
      clearPending();
      return null;
    }
    return p;
  } catch {
    clearPending();
    return null;
  }
};
